import type { Participant } from "@/types/Participant";
import type { Pass } from "@/types/Pass";
import type { PassType } from "@/types/PassType";
import type { PricingPlan } from "@/types/PricingPlan";
import type { UserPassWithDetails } from "@/types/UserPass";
import { create } from "zustand";

export type PricingItem = {
  id: string;
  title: string;
  description?: string | null;
  price: number;
  classes: number | null;
  type: "PASS" | "SUBSCRIPTION";
  interval?: string | null;
  intervalCount?: number | null;
};

export type BookingSessionDetails = {
  id: string;
  studioId?: string;
  courseId?: string;
  title: string;
  description?: string | null;
  image?: string | null;
  color?: string | null;
  tags?: string[] | null;
  startAt: Date | string;
  endAt: Date | string;
  teacherId?: string | null;
  teacherName?: string | null;
  capacity?: number | null;
  room?: string | null;
  isCancelled?: boolean;
  bookable?: boolean;
  status?: string;
  durationMin?: number | null;
  timezone?: string | null;
  bookingMode?: string | null;
  allowDropIn?: boolean;
  allowGuests?: boolean;
  allowPayWhatYouWant?: boolean;
  dropInPrice?: number | null;
  visibleInSchedule?: boolean;
  isActive?: boolean;
  recurrenceRule?: string | null;
  recurrenceEnd?: Date | string | null;
  createdAt?: Date | string;
  pricing?: {
    passes: Pass[];
    // subscriptions: Subscription[];
  };
  teacher?: {
    id: string;
    name: string | null;
    avatar?: string | null;
  } | null;
};

// Define types for extended objects
export interface PricingPlanAssignmentWithPlan {
  id: string;
  userId: string;
  planId: string;
  categoryId: string;
  startDate: Date;
  endDate: Date;
  status: string;
  credits: number | null;
  plan: PricingPlan;
}

export interface PassHoldingWithType {
  id: string;
  userId: string;
  passTypeId: string;
  purchaseDate: Date;
  expiryDate: Date;
  classesLeft: number | null;
  status: string;
  passType: PassType & {
    category: {
      id: string;
      name: string;
    };
  };
}

export interface DiscountInfo {
  id: string;
  code: string;
  description?: string;
  discountAmount: string;
  originalAmount: string;
  newAmount: string;
  // Un code promo part en `discountCodeId`, une carte cadeau en
  // `giftCardCode` : le serveur les cherche dans deux tables differentes.
  // Sans cette distinction, l'identifiant d'une carte cadeau etait envoye
  // comme code promo, introuvable, et la reservation echouait apres que la
  // reduction ait ete affichee.
  type?: "DISCOUNT_CODE" | "GIFT_CARD";
  remainingAfter?: string;
  currency?: string;
}

export const BookingMethod = {
  USER_PASS: "USER_PASS",
  DROP_IN: "DROP_IN",
  NEW_PASS: "NEW_PASS",
};

export const PaymentMethods = {
  CASH: "CASH",
  TRANSFER: "TRANSFER",
  CARD: "CARD",
  PAYPAL: "PAYPAL",
  TWINT: "TWINT",
  ADDITIONAL: "ADDITIONAL",
  POSTFINANCE: "POSTFINANCE",
  PASS: "PASS",
};

interface BookingState {
  // Selected event to book
  selectedEvent: BookingSessionDetails | null;

  // Booking method - how the user wants to book the class
  bookingMethod: keyof typeof BookingMethod;

  // Pass-related state
  selectedPass: Pass | null;
  selectedUserPass: UserPassWithDetails | null;
  selectedPassId: string | null;

  // Payment method info
  paymentMethod: string | null;

  // Discount code related state
  discountCode: string | null;
  discountInfo: DiscountInfo | null;
  isApplyingDiscount: boolean;
  discountError: string | null;

  // Participants
  participants: Partial<Participant>[];

  // Store actions
  setSelectedEvent: (event: BookingSessionDetails | null) => void;
  setBookingMethod: (method: keyof typeof BookingMethod) => void;
  // Pass actions
  setSelectedPass: (pass: Pass | null) => void;
  setSelectedUserPass: (pass: UserPassWithDetails | null) => void;
  setSelectedPassId: (id: string | null) => void;

  // Payment method
  setPaymentMethod: (method: string | null) => void;

  // Discount code actions
  setDiscountCode: (code: string | null) => void;
  setDiscountInfo: (info: DiscountInfo | null) => void;
  setIsApplyingDiscount: (isApplying: boolean) => void;
  setDiscountError: (error: string | null) => void;
  clearDiscount: () => void;

  // Reset store
  resetBookingState: () => void;

  // Participants actions
  setParticipants: (participants: Partial<Participant>[]) => void;
  addParticipant: (participant: Partial<Participant>) => void;
  removeParticipant: (participant: Partial<Participant>) => void;
}

const useBookingStore = create<BookingState>()((set, get) => ({
  selectedEvent: null,
  bookingMethod: "DROP_IN" as const,

  // Pass state
  selectedPass: null,
  selectedUserPass: null,
  selectedPassId: null,

  // Payment method
  paymentMethod: PaymentMethods.CASH,

  // Discount code state
  discountCode: null,
  discountInfo: null,
  isApplyingDiscount: false,
  discountError: null,

  // participants
  participants: [],

  // Actions
  setSelectedEvent: (event) => set({ selectedEvent: event }),
  setBookingMethod: (method) => set({ bookingMethod: method }),

  // Pass actions
  setSelectedPass: (pass) => set({ selectedPass: pass }),
  setSelectedUserPass: (pass) => set({ selectedUserPass: pass }),
  setSelectedPassId: (id) => set({ selectedPassId: id }),

  // Payment method
  setPaymentMethod: (method) => set({ paymentMethod: method }),

  // Discount code actions
  setDiscountCode: (code) => set({ discountCode: code }),
  setDiscountInfo: (info) => set({ discountInfo: info }),
  setIsApplyingDiscount: (isApplying) =>
    set({ isApplyingDiscount: isApplying }),
  setDiscountError: (error) => set({ discountError: error }),
  clearDiscount: () =>
    set({
      discountCode: null,
      discountInfo: null,
      discountError: null,
    }),

  // Participants
  setParticipants: (participants) => set({ participants }),
  addParticipant: (participant) => {
    const state = get();
    set({ participants: [...state.participants, participant] });
  },
  removeParticipant: (participant) => {
    const state = get();

    set({
      participants: state.participants.filter((p) => p.id !== participant.id),
    });
  },

  // Reset store
  resetBookingState: () =>
    set({
      selectedPass: null,
      selectedUserPass: null,
      selectedPassId: null,
      bookingMethod: "DROP_IN" as const,
      paymentMethod: PaymentMethods.CASH,
      discountCode: null,
      discountInfo: null,
      discountError: null,
    }),
}));

export default useBookingStore;
